import { BACKEND_URL } from '../../../utils/config';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      // Try to get token from Redux state first
      const state = getState();
      let token;
      
      if (state.auth && state.auth.token) {
        token = state.auth.token;
      } else {
        // Fallback to localStorage
        token = localStorage.getItem('token');
      }
      
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['StripeAccount', 'AccountStatus', 'AccountBalance', 'Orders', 'PlatformPendingProductEarnings', 'MerchantEarningsSummary', 'SavedPaymentMethods', 'MerchantFundCollectionPayments'],
  endpoints: (builder) => ({
    // Stripe Connect endpoints
    createStripeAccount: builder.mutation({
      query: (data = {}) => ({
        url: '/stripe/create-account',
        method: 'POST',
        body: data // Pass any data including country
      }),
      invalidatesTags: ['StripeAccount'],
      transformResponse: (response) => {
        return response;
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
        } catch (error) {
          console.error('Error creating Stripe account:', error);
        }
      },
    }),
    createAccountSession: builder.mutation({
      query: (data) => {
        // Handle both string accountId or object with parameters
        const isString = typeof data === 'string';
        
        return {
          url: '/stripe/account-session',
          method: 'POST',
          body: isString 
            ? { account: data } // Old format: just the account ID
            : data // New format: object with account, available_countries, etc.
        };
      },
      transformResponse: (response) => {
        return response;
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
        } catch (error) {
          console.error('Error creating account session:', error);
        }
      },
    }),
    getAccountStatus: builder.query({
      query: () => '/stripe/account-status',
      providesTags: ['AccountStatus'],
      transformResponse: (response) => {
        if (!response || !response.success) {
          console.warn('Received unexpected response format:', response);
          return {
            status: {
              detailsSubmitted: false,
              chargesEnabled: false,
            },
            error: 'Unexpected response format'
          };
        }
        return response;
      },
      transformErrorResponse: (response, meta, arg) => {
        console.error('Error retrieving account status:', response);
        
        // Extract the specific error message if available
        let errorMessage = 'Failed to retrieve account status';
        try {
          if (response.data && response.data.error) {
            errorMessage = response.data.error;
          } else if (typeof response.error === 'string') {
            errorMessage = response.error;
          } else if (response.status === 404) {
            errorMessage = 'Endpoint not found (404). Check API configuration.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          }
        } catch (err) {
          console.error('Error parsing error response:', err);
        }
        
        // Return a default status object for UI handling
        return {
          status: {
            detailsSubmitted: false,
            chargesEnabled: false
          },
          error: errorMessage
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
        } catch (error) {
          console.error('Error getting account status:', error);
          // Errors are handled by transformErrorResponse
        }
      },
    }),
    getAccountBalance: builder.query({
      query: (args) => {
        // Ensure args is an object and provide defaults
        const { start = 0, limit = 10 } = args || {};
        return {
        url: '/stripe/account-balance',
        params: { _start: start, _limit: limit }
        };
      },
      providesTags: ['AccountBalance'],
      transformResponse: (response) => {
        if (!response || !response.success) {
          console.warn('Received unexpected balance response format:', response);
          return {
            balance: {
              available: [],
              pending: []
            },
            transactions: {
              data: [],
              pagination: { start: 0, limit: 10, total: 0, hasMore: false }
            },
            error: 'Unexpected response format'
          };
        }
        return response;
      },
      transformErrorResponse: (response, meta, arg) => {
        console.error('Error retrieving account balance:', response);
        
        // Extract the specific error message if available
        let errorMessage = 'Failed to retrieve account balance';
        try {
          if (response.data && response.data.error) {
            errorMessage = response.data.error;
          } else if (typeof response.error === 'string') {
            errorMessage = response.error;
          } else if (response.status === 404) {
            errorMessage = 'Balance endpoint not found (404). Check API configuration.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          }
        } catch (err) {
          console.error('Error parsing balance error response:', err);
        }
        
        // Return a default balance object for UI handling
        return {
          balance: {
            available: [],
            pending: []
          },
          transactions: {
            data: [],
            pagination: { start: 0, limit: 10, total: 0, hasMore: false }
          },
          error: errorMessage
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
        } catch (error) {
          console.error('Error getting account balance:', error);
          // Errors are handled by transformErrorResponse
        }
      },
    }),

    // Payment processing endpoints
    createPaymentIntent: builder.mutation({
      query: (data) => ({
        url: '/payments/create-payment-intent',
        method: 'POST',
        body: {
          amount: data.amount,
          currency: data.currency || 'usd',
          transfer_group: data.orderId ? `ORDER_${data.orderId}` : undefined,
          metadata: data.metadata || {}
        },
      }),
    }),
    confirmPayment: builder.mutation({
      query: ({ orderId, paymentIntentId }) => ({
        url: `/orders/${orderId}`,
        method: 'PUT',
        body: {
          paymentStatus: 'paid',
          stripePaymentIntentId: paymentIntentId,
        },
      }),
    }),
    fetchPaymentMethods: builder.query({
      query: (userId) => `/users/${userId}/payment-methods`,
    }),
    refundPayment: builder.mutation({
      query: (data) => ({
        url: '/payments/refund',
        method: 'POST',
        body: data,
      }),
    }),

    // New endpoints for multi-merchant payments and transfers
    createMultiMerchantPaymentIntent: builder.mutation({
      query: (data) => ({
        url: '/payment/create-multi-merchant-payment-intent',
        method: 'POST',
        body: {
          cartItems: data.cartItems,
          currency: data.currency || 'usd',
          orderId: data.orderId,
          shippingCost: data.shippingCost
        },
      }),
    }),
    
    // Get payments for specific order
    getPaymentsByOrderId: builder.query({
      query: (orderId) => `/payment/order/${orderId}`,
    }),
    
    // Order status update (which will trigger automatic transfers when completed)
    updateOrderStatus: builder.mutation({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/status`,
        method: 'PUT',
        body: { status }
      }),
      invalidatesTags: ['Orders'],
    }),

    getPlatformPendingProductEarnings: builder.query({
      query: () => '/payment/me/platform-pending-product-earnings',
      providesTags: (result, error, arg) =>
        result
          ? [{ type: 'PlatformPendingProductEarnings', id: 'LIST' }]
          : [{ type: 'PlatformPendingProductEarnings', id: 'LIST' }],
      transformResponse: (response) => {
        // console.log('Get Platform Pending Product Earnings response:', response);
        return response;
      },
    }),

    getMerchantEarningsSummary: builder.query({
      query: () => '/payment/me/merchant-earnings-summary',
      providesTags: (result) =>
        result
          ? [{ type: 'MerchantEarningsSummary', id: 'LIST' }]
          : [{ type: 'MerchantEarningsSummary', id: 'LIST' }],
      transformResponse: (response) => response,
    }),

    // Add these endpoints to the paymentApi.endpoints object
    getSavedPaymentMethods: builder.query({
      query: () => ({
        url: `/payment-methods`,
        method: 'GET',
      }),
      transformResponse: (response) => response,
      providesTags: ['SavedPaymentMethods'],
    }),

    createSetupIntent: builder.mutation({
      query: () => ({
        url: `/payment-methods/setup-intent`,
        method: 'POST',
      }),
    }),

    savePaymentMethod: builder.mutation({
      query: (data) => ({
        url: `/payment-methods`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    deletePaymentMethod: builder.mutation({
      query: (id) => ({
        url: `/payment-methods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    setDefaultPaymentMethod: builder.mutation({
      query: (id) => ({
        url: `/payment-methods/${id}/set-default`,
        method: 'PUT',
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    getReturnLabelQuote: builder.query({
      query: (orderItemId) => ({
        url: `/returns/${orderItemId}/label-quote`,
        method: 'GET',
      }),
    }),

    processReturnLabelPayment: builder.mutation({
      query: ({ orderItemId, ...data }) => ({
        url: `/returns/${orderItemId}/process-label-payment`,
        method: 'POST',
        body: data,
      }),
    }),

    generateReturnLabel: builder.mutation({
      query: ({ orderItemId, fulfillmentGroupId, fromAddressId, toAddressId, rateId, paymentMethod, amount, currency, isReturn = false, parcels }) => ({
        // Redirect to the delivery endpoint instead of the payment endpoint
        url: `/delivery/create-return-shipping-label`,
        method: 'POST',
        body: { 
          fulfillmentGroupId,
          orderItemId,
          fromAddressId, 
          toAddressId, 
          rateId, 
          paymentMethod, 
          amount, 
          currency,
          isReturn,
          parcels
        }
      }),
      invalidatesTags: (result) => result?.success ? 
        [
          { type: 'OrderItem', id: 'LIST' },
          { type: 'FulfillmentGroup', id: 'LIST' },
          { type: 'Request', id: 'LIST' }
        ] : []
    }),

    // Merchant fund collection endpoints
    createFundCollectionIntent: builder.mutation({
      query: (data) => ({
        url: '/merchant/create-fund-collection-intent',
        method: 'POST',
        body: {
          amount: data.amount,
          currency: data.currency,
          fulfillmentGroupId: data.fulfillmentGroupId,
          description: data.description,
          savedPaymentMethodId: data.savedPaymentMethodId
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error creating fund collection intent:', response);
        return response;
      }
    }),

    confirmFundCollection: builder.mutation({
      query: (data) => ({
        url: '/merchant/confirm-fund-collection',
        method: 'POST',
        body: {
          paymentIntentId: data.paymentIntentId,
          fulfillmentGroupId: data.fulfillmentGroupId,
          maintenanceFee: data.maintenanceFee,
          returnToAddress: data.returnToAddress
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error confirming fund collection:', response);
        return response;
      },
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        'AccountBalance', 
        'MerchantFundCollectionPayments',
        // Add fulfillment group cache invalidation so payment status is refreshed
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'OrderItem', id: 'LIST' }
      ]
    }),

    createShippingDifferencePayment: builder.mutation({
      query: (data) => ({
        url: '/payments/shipping-difference',
        method: 'POST',
        body: data,
      }),
    }),

    refundShippingDifference: builder.mutation({
      query: (data) => ({
        url: '/payments/refund-shipping-difference',
        method: 'POST',
        body: data,
      }),
    }),

    debitMerchantBalance: builder.mutation({
      query: (data) => ({
        url: '/merchant/debit-balance',
        method: 'POST',
        body: {
          amount: data.amount,
          currency: data.currency,
          fulfillmentGroupId: data.fulfillmentGroupId,
          maintenanceFee: data.maintenanceFee,
          returnToAddress: data.returnToAddress,
        },
      }),
      invalidatesTags: ['AccountBalance', 'MerchantFundCollectionPayments'],
    }),

    getMerchantFundCollectionPayments: builder.query({
      query: ({ start = 0, limit = 10 } = {}) => ({
        url: '/merchant/fund-collection-payments',
        params: { _start: start, _limit: limit }
      }),
      transformResponse: (response) => {
        if (!response || !response.success) {
          return {
            payments: {
              data: [],
              pagination: { start: 0, limit: 10, total: 0, hasMore: false }
            }
          };
        }
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error fetching merchant fund collection payments:', response);
        return {
          payments: {
            data: [],
            pagination: { start: 0, limit: 10, total: 0, hasMore: false }
          },
          error: response.data?.error || 'Failed to load payments'
        };
      },
      providesTags: ['MerchantFundCollectionPayments']
    }),
  }),
});

export const {
  useCreateStripeAccountMutation,
  useCreateAccountSessionMutation,
  useGetAccountStatusQuery,
  useGetAccountBalanceQuery,
  useCreatePaymentIntentMutation,
  useConfirmPaymentMutation,
  useFetchPaymentMethodsQuery,
  useRefundPaymentMutation,
  useCreateMultiMerchantPaymentIntentMutation,
  useGetPaymentsByOrderIdQuery,
  useUpdateOrderStatusMutation,
  useGetPlatformPendingProductEarningsQuery,
  useGetSavedPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useGetReturnLabelQuoteQuery,
  useProcessReturnLabelPaymentMutation,
  useGenerateReturnLabelMutation,
  useCreateFundCollectionIntentMutation,
  useConfirmFundCollectionMutation,
  useCreateShippingDifferencePaymentMutation,
  useRefundShippingDifferenceMutation,
  useDebitMerchantBalanceMutation,
  useGetMerchantFundCollectionPaymentsQuery,
  useGetMerchantEarningsSummaryQuery,
} = paymentApi;

// Add the new prepareCheckout mutation separately to avoid merge conflicts if file is large
// This is a common pattern if the builder callback gets too extensive.
// However, for typical RTK Query, you'd add it within the same builder.endpoints callback.
// For simplicity here, let's assume it's added to the existing builder.

export const paymentApiWithPrepareCheckout = paymentApi.injectEndpoints({
  endpoints: (builder) => ({
    prepareCheckout: builder.mutation({
      query: (payload) => ({
        url: '/payments/prepare-checkout',
        method: 'POST',
        body: payload, // { shippableItems, shippingAddressId, selectedShippingRatesDetails, targetCurrency }
      }),
      // We might want to invalidate or update something upon success/failure if needed
      // For now, it primarily returns clientSecret and strapiPaymentId
    }),
  }),
  overrideExisting: false, // Important if injecting into an existing API slice
});

export const {
  usePrepareCheckoutMutation,
  // Re-export existing hooks if you are creating a new enhanced api instance.
  // If modifying directly, this section might not change much.
} = paymentApiWithPrepareCheckout;

// Add a new mutation for removing purchased items from cart
export const paymentApiWithRemovePurchased = paymentApi.injectEndpoints({
  endpoints: (builder) => ({
    removePurchasedItemsFromCart: builder.mutation({
      query: (payload) => ({ // payload: { purchasedItems, currency }
        url: '/carts/remove-purchased',
        method: 'POST',
        body: payload,
      }),
      // Optionally, invalidate cart tags if you have them in cartApi
      // invalidatesTags: [{ type: 'Cart', id: 'ME' }], 
    }),
  }),
  overrideExisting: false,
});

export const {
  useRemovePurchasedItemsFromCartMutation,
} = paymentApiWithRemovePurchased;

// Platform-based transaction and refund endpoints (not Stripe)
export const paymentApiWithPlatformTransactions = paymentApi.injectEndpoints({
  endpoints: (builder) => ({
    getMerchantTransactions: builder.query({
      query: ({ start = 0, limit = 10 } = {}) => ({
        url: '/payment/me/transactions',
        params: { _start: start, _limit: limit }
      }),
      transformResponse: (response) => {
        if (!response || !response.success) {
          return {
            transactions: {
              data: [],
              pagination: { start: 0, limit: 10, total: 0, hasMore: false }
            }
          };
        }
        return response;
      },
      providesTags: ['MerchantTransactions']
    }),
    getMerchantRefundPayments: builder.query({
      query: ({ start = 0, limit = 10 } = {}) => ({
        url: '/payment/me/refund-payments',
        params: { _start: start, _limit: limit }
      }),
      transformResponse: (response) => {
        if (!response || !response.success) {
          return {
            payments: {
              data: [],
              pagination: { start: 0, limit: 10, total: 0, hasMore: false }
            }
          };
        }
        return response;
      },
      providesTags: ['MerchantRefundPayments']
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMerchantTransactionsQuery,
  useGetMerchantRefundPaymentsQuery,
} = paymentApiWithPlatformTransactions;

export const enhancedPaymentApi = paymentApiWithPlatformTransactions;
